import { LegacyPortalDashboardSalesAggregationUnit } from "@spwn-portal/grpc";

import type { SalesDashboardDateRangeInputValue } from "./SalesDashboardDateRangeInputs";
import type { TicketSalesByOrderDate } from "./useGetTicketSalesByOrderDate";

type SalesDashboardDateRange = {
  fromDate: bigint;
  toDate: bigint;
};

const maximumMinuteAggregationDuration = BigInt(120 * 60 * 1000);
const minimumDayAggregationDuration = BigInt(48 * 60 * 60 * 1000);

// 閲覧端末のタイムゾーンに左右されず、売上ダッシュボードではJSTで表示する。
const dateTimeFormatter = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: "Asia/Tokyo",
});

/**
 * `datetime-local`にはタイムゾーンを含められないため、Unix timeをJSTの
 * `yyyy-MM-ddTHH:mm`形式へ変換する。
 */
const formatDateTimeInputValue = (timestamp: number): string => {
  const parts = dateTimeFormatter.formatToParts(new Date(timestamp));
  const getPartValue = (type: string): string =>
    parts.find((part) => part.type === type)?.value ?? "";

  return `${getPartValue("year")}-${getPartValue("month")}-${getPartValue(
    "day"
  )}T${getPartValue("hour")}:${getPartValue("minute")}`;
};

const formatDateInputValue = (timestamp: number): string =>
  formatDateTimeInputValue(timestamp).slice(0, 10);

export const formatSalesDashboardDateTime = (timestamp: bigint): string => {
  const inputValue = formatDateTimeInputValue(Number(timestamp));

  return inputValue.replace(/-/g, "/").replace("T", " ");
};

/**
 * DAY集計の最古・最新日を、日単位の日付入力初期値へ変換する。
 * 売上がない場合は`undefined`を返し、入力欄を空表示にする。
 */
export const createSalesDashboardDateRangeInputValue = (
  ticketSalesByOrderDate: readonly TicketSalesByOrderDate[]
): SalesDashboardDateRangeInputValue | undefined => {
  const [firstTicketSale] = ticketSalesByOrderDate;

  if (!firstTicketSale) {
    return undefined;
  }

  const dateRange = ticketSalesByOrderDate.reduce(
    (range, ticketSale) => ({
      start:
        ticketSale.orderDate < range.start ? ticketSale.orderDate : range.start,
      end: ticketSale.orderDate > range.end ? ticketSale.orderDate : range.end,
    }),
    {
      start: firstTicketSale.orderDate,
      end: firstTicketSale.orderDate,
    }
  );

  return {
    startDateTime: `${formatDateInputValue(Number(dateRange.start))}T00:00`,
    endDateTime: `${formatDateInputValue(Number(dateRange.end))}T23:59`,
  };
};

const parseDateTimeInputValue = (dateTime: string): bigint | undefined => {
  if (!dateTime) {
    return undefined;
  }

  const timestamp = Date.parse(`${dateTime}+09:00`);

  return Number.isNaN(timestamp) ? undefined : BigInt(timestamp);
};

export const createSalesDashboardDateRange = (
  value: SalesDashboardDateRangeInputValue
): SalesDashboardDateRange | undefined => {
  const fromDate = parseDateTimeInputValue(value.startDateTime);
  const toDate = parseDateTimeInputValue(value.endDateTime);

  if (fromDate === undefined || toDate === undefined || fromDate > toDate) {
    return undefined;
  }

  return { fromDate, toDate };
};

export const getSalesDashboardAggregationUnit = (
  dateRange: SalesDashboardDateRange | undefined
): LegacyPortalDashboardSalesAggregationUnit => {
  if (!dateRange) {
    return LegacyPortalDashboardSalesAggregationUnit.DAY;
  }

  const duration = dateRange.toDate - dateRange.fromDate;

  if (duration <= maximumMinuteAggregationDuration) {
    return LegacyPortalDashboardSalesAggregationUnit.MINUTE;
  }

  if (duration < minimumDayAggregationDuration) {
    return LegacyPortalDashboardSalesAggregationUnit.HOUR;
  }

  return LegacyPortalDashboardSalesAggregationUnit.DAY;
};
